import { useEffect, useState, useCallback, useRef } from 'react';
import { createContainer } from 'unstated-next';
import { AgentInfo, RoomResData } from '../type.js';
import { AgentItem, TSessionListItem } from '../sidebar/type.js';
import { modalConfirm, Toast } from '@kim/ui';
import { FromIdRoomResDataServer } from '../type.js';
import {
  getConversationListApi,
  postConversationRenameApi,
  deleteConversationApi,
  postConversationShareApi,
  getConversationDetailApi,
  postConversationDetailsApi,
  getConversationPageListApi,
} from '@src/services/walle';
import { GlobalEventEmitter } from '@/pages/pc/globalEventListner.js';
import { KIM_EVENT } from '@/pages/pc/eventChannel.js';
import intl from '@/internationalzation/intl.js';
import { useLocation, useNavigate } from 'react-router-dom';
// import i18n from '@kim/i18n';
import copy from 'copy-to-clipboard';
import { sendKimEvent } from '@/pages/pc/kim';
import { Message } from '@docs/kd-d2c-components';
const CONVERSATION_ID_KEY = 'conversationId';
// const
interface IAIWindowContextState {
  app: any;
}

const DEFAULT_PAGE_INFO = {
  page: 0,
  pageSize: 100,
  hasMore: true,
};
function isRunning(status: string) {
  return ['running', 'pending'].includes(status || '');
}
const KIM_API_URL = 'https://mario.corp.kuaishou.com';
// 一些需要存储的变量可以通过context 拿到
const useAIWindow = (initialState?: IAIWindowContextState) => {
  const { app } = initialState || {};
  const webviewRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const baseUrl = KIM_API_URL;
  const [isDarkMode, setIsDarkMode] = useState(
    window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  const [url, setUrl] = useState<string>(baseUrl);
  const [conversionId, setConversionId] = useState<string>(
    sessionStorage.getItem(CONVERSATION_ID_KEY) || '',
  );
  const [agentList, setAgentList] = useState<AgentItem[]>([]);

  // 会话列表数据
  const [conversationList, setConversationList] = useState<TSessionListItem[]>(
    [],
  );
  const [unreadConversationIds, setUnreadConversationIds] = useState<string[]>(
    [],
  );
  // 正在运行的会话id列表（ref + state，state 用于 effect 依赖确保轮询正确启动）
  const runningConversationIdsRef = useRef<string[]>([]);
  const [runningConversationIds, setRunningConversationIds] = useState<
    string[]
  >([]);
  const runningConversationInfo = useRef<Record<string, any>>({});
  const runningCountRef = useRef<number>(0);
  const [runningTag, setRunningTag] = useState<number>(0);
  // 会话列表 分页信息
  const [conversationPageInfo, setConversationPageInfo] = useState<{
    page: number;
    pageSize: number;
    hasMore: boolean;
  }>({ ...DEFAULT_PAGE_INFO });
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const prevConversationStatusMapRef = useRef<Record<string, string>>({});
  const currentConversionIdRef = useRef<string>(
    sessionStorage.getItem(CONVERSATION_ID_KEY) || '',
  );
  // 用户已查看过的会话（点进后清除过未读），这些会话完成时不再重复打未读红点，直到有新任务开始
  const readConversationIdsRef = useRef<Set<string>>(new Set());

  const getConversationUrl = useCallback(
    (conversationId: string) => {
      return `${baseUrl}/c/${conversationId}`;
    },
    [baseUrl],
  );

  const toConversion = useCallback(
    (id: string = '') => {
      if (!id) {
        setUrl(`${baseUrl}`);
        setConversionId('');
        currentConversionIdRef.current = '';
        sessionStorage.removeItem(CONVERSATION_ID_KEY);
        navigate('/mario');
      } else {
        setUrl(getConversationUrl(id));
        setConversionId(id);
        currentConversionIdRef.current = id;
        setUnreadConversationIds((prev) => prev.filter((item) => item !== id));
        readConversationIdsRef.current.add(id);
        sessionStorage.setItem(CONVERSATION_ID_KEY, id);
        const conversationTag = conversationList.find(
          (item) => item.conversationId === id,
        )?.tag;

        // 判断当前路径是否已经是目标路径，避免清空 state
        const currentPath = window.location.pathname;
        // 只有审计模式才有conversationTag
        const targetPath = `/c/${id}${!!conversationTag ? '?audit=1' : ''}${!!conversationTag ? `&audit_detail_mode=${conversationTag}` : ''}`;

        if (currentPath !== targetPath) {
          navigate(targetPath);
        }
      }
    },
    [baseUrl, navigate, conversationList],
  );

  const changeUrl = useCallback(
    (url: string, conversionId?: string) => {
      const id = conversionId || '';
      setUrl(`${baseUrl}${url}`);
      setConversionId(id);
      currentConversionIdRef.current = id;
      if (id) {
        sessionStorage.setItem(CONVERSATION_ID_KEY, id);
        setUnreadConversationIds((prev) => prev.filter((item) => item !== id));
        readConversationIdsRef.current.add(id);
      } else {
        sessionStorage.removeItem(CONVERSATION_ID_KEY);
      }
    },
    [baseUrl],
  );

  const agentClickhandler = useCallback(async (item: AgentItem) => {
    sendKimEvent(KIM_EVENT.OPEN_AGENT_WINDOW, item, undefined, true);
  }, []);

  const getAgentList = useCallback(() => {
    sendKimEvent(
      KIM_EVENT.GET_AGENT_LIST,
      { pageNumber: 1, pageSize: 2000 },
      async (result: any) => {
        setAgentList(result.list || []);
      },
      true,
    );
  }, []);

  // 删除 agent
  const deleteAgent = useCallback((item: AgentItem) => {
    sendKimEvent(
      KIM_EVENT.DELETE_AGENT,
      item,
      () => {
        getAgentList();
      },
      true,
    );
  }, []);

  const loadConversations = useCallback(
    async (
      isFirst: boolean = false,
      pageInfo: {
        page: number;
        pageSize: number;
        hasMore: boolean;
      } = DEFAULT_PAGE_INFO,
      beforeCb?: () => void,
      afterCb?: () => void,
    ) => {
      if (!pageInfo.hasMore) return;
      beforeCb?.();
      // 获取会话列表
      const data = (
        await getConversationPageListApi({
          page: pageInfo.page + 1,
          page_size: pageInfo.pageSize,
        })
      ).data as any;

      if (!data) {
        setConversationPageInfo((prev) => {
          return {
            ...prev,
            hasMore: false,
          };
        });
        afterCb?.();
        return;
      }
      runningConversationInfo.current = {};
      setConversationList((prev) => {
        const newList: TSessionListItem[] = isFirst
          ? ((data.items || []) as TSessionListItem[])
          : [...prev, ...((data.items || []) as TSessionListItem[])];
        // 更新正在运行的会话id列表
        const runningConversationIds = newList
          .filter((item) => isRunning(item.status || ''))
          .map((item) => {
            const conversationId = item.conversationId || '';
            runningConversationInfo.current[conversationId] = item;
            return conversationId;
          });

        runningConversationIdsRef.current = runningConversationIds;
        setRunningConversationIds(runningConversationIds);
        runningConversationIds.forEach((id) =>
          readConversationIdsRef.current.delete(id),
        );
        return newList;
      });
      setConversationPageInfo({
        page: data.page || 0,
        pageSize: data.page_size || 0,
        hasMore: (data.page || 0) * (data.page_size || 0) < (data.total || 0),
      });
      afterCb?.();
    },
    [],
  );

  // 重命名会话
  const renameConversation = useCallback(async (id: string, name: string) => {
    const success = await postConversationRenameApi({
      conversationId: id,
      conversationName: name,
    });
    if (success) {
      setConversationList((prev) => {
        return prev.map((item) =>
          item.conversationId === id
            ? { ...item, conversationName: name }
            : item,
        );
      });
    }
  }, []);

  // 删除会话
  const deleteConversation = useCallback(
    async (id: string) => {
      const success = await deleteConversationApi({ conversationId: id });
      if (success) {
        setConversationList((prev) => {
          return prev.filter((item) => item.conversationId !== id);
        });
        if (runningConversationIdsRef.current.includes(id)) {
          const newRunningConversationIds =
            runningConversationIdsRef.current.filter((item) => item !== id);
          runningConversationIdsRef.current = newRunningConversationIds;
          setRunningConversationIds(newRunningConversationIds);
        }
        if (conversionId === id) {
          toConversion('');
        }
      }
    },
    [conversionId, runningConversationIdsRef.current],
  );

  // 分享会话
  const shareConversation = useCallback(async (id: string) => {
    const success = await postConversationShareApi(
      { conversationId: id },
      {
        shareType: 'PUBLIC',
        shareDetail: [],
      },
    );
    if (success) {
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      url.searchParams.set('replay', '1');
      copy(url.toString() || '', {
        format: 'text/plain',
      });
      Message.success({
        duration: 2000,
        message: intl.t('复制链接成功，快去分享给他人吧'),
      });
      return url.toString();
    }
  }, []);

  useEffect(() => {
    getAgentList();
  }, []);

  useEffect(() => {
    loadConversations(true);
  }, []);

  useEffect(() => {
    const match = location.pathname.match(/^\/c\/([^/?]+)/);
    const nextConversionId = match?.[1] || '';
    setConversionId((prev) => {
      if (prev === nextConversionId) {
        return prev;
      }
      return nextConversionId;
    });
    if (nextConversionId) {
      currentConversionIdRef.current = nextConversionId;
      sessionStorage.setItem(CONVERSATION_ID_KEY, nextConversionId);
      return;
    }
    currentConversionIdRef.current = '';
    sessionStorage.removeItem(CONVERSATION_ID_KEY);
  }, [location.pathname]);

  useEffect(() => {
    if (!conversionId) return;
    setUnreadConversationIds((prev) =>
      prev.filter((conversationId) => conversationId !== conversionId),
    );
  }, [conversionId]);

  useEffect(() => {
    let isMounted = true;
    const prefersDarkScheme = window.matchMedia('(prefers-color-scheme: dark)');
    prefersDarkScheme.addEventListener('change', (event) => {
      if (!isMounted) return;
      setIsDarkMode(event.matches);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!conversationList?.length) {
      prevConversationStatusMapRef.current = {};
      return;
    }
    const nextStatusMap: Record<string, string> = {};
    const completedConversationIds: string[] = [];
    conversationList.forEach((item) => {
      const id = item.conversationId || '';
      if (!id) return;
      const nextStatus = item.status || '';
      const prevStatus = prevConversationStatusMapRef.current[id] || '';
      if (
        isRunning(prevStatus) &&
        !isRunning(nextStatus) &&
        id !== currentConversionIdRef.current
      ) {
        completedConversationIds.push(id);
      }
      nextStatusMap[id] = nextStatus;
    });
    prevConversationStatusMapRef.current = nextStatusMap;
    const toAdd = completedConversationIds.filter(
      (id) => !readConversationIdsRef.current.has(id),
    );
    if (!toAdd.length) return;
    setUnreadConversationIds((prev) => {
      const unreadSet = new Set(prev);
      toAdd.forEach((id) => unreadSet.add(id));
      return Array.from(unreadSet);
    });
  }, [conversationList, conversionId]);

  const getConversationDetail = (conversationId: string) => {
    readConversationIdsRef.current.delete(conversationId);
    let currentConversationIds: string[] = [];
    if (runningConversationIdsRef.current.includes(conversationId)) {
      currentConversationIds = [...runningConversationIdsRef.current];
    } else {
      currentConversationIds = [
        conversationId,
        ...runningConversationIdsRef.current,
      ];
    }

    postConversationDetailsApi({}, { conversationIds: [conversationId] }).then(
      (res: any) => {
        if (res.data) {
          updateFn(res.data);
          runningConversationIdsRef.current = currentConversationIds;
          setRunningConversationIds(currentConversationIds);
          setRunningTag((prevTag) => prevTag + 1);
        }
      },
    );
  };

  const updateFn = (data: TSessionListItem[]) => {
    let hasChanged = false;
    const newRunningConversationIds: string[] = [];
    const prevRunningConversationIds = [...runningConversationIdsRef.current];
    data.forEach((item) => {
      const conversationId = item.conversationId || '';
      const oldItem = runningConversationInfo.current[conversationId];
      const isNowRunning = isRunning(item.status || '');
      // 状态变化 或者icon 变化，都算变了，需要更新
      if (
        oldItem?.status !== item.status ||
        oldItem?.icon?.light !== item?.icon?.light ||
        oldItem?.icon?.dark !== item?.icon?.dark
      ) {
        hasChanged = true;
        runningConversationInfo.current[conversationId] = item;
      }
      if (isNowRunning) {
        newRunningConversationIds.push(conversationId);
      }
    });
    const completedConversationIds = prevRunningConversationIds.filter(
      (id) =>
        id &&
        !newRunningConversationIds.includes(id) &&
        id !== currentConversionIdRef.current,
    );
    const toAdd = completedConversationIds.filter(
      (id) => !readConversationIdsRef.current.has(id),
    );
    if (toAdd.length) {
      setUnreadConversationIds((prev) => {
        const unreadSet = new Set(prev);
        toAdd.forEach((id) => unreadSet.add(id));
        return Array.from(unreadSet);
      });
    }
    newRunningConversationIds.forEach((id) =>
      readConversationIdsRef.current.delete(id),
    );
    runningConversationIdsRef.current = newRunningConversationIds;
    setRunningConversationIds(newRunningConversationIds);
    if (hasChanged) {
      setConversationList((prev) => {
        return prev.map((item) => {
          const conversationId = item.conversationId || '';
          return runningConversationInfo.current[conversationId] || item;
        });
      });
    }
  };
  useEffect(() => {
    clearTimer();
    if (!runningConversationIds.length) return;
    let currentConversationIds: string[] = [...runningConversationIds];
    function clearTimer() {
      runningCountRef.current = 0;
      runningConversationInfo.current = {};
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    function update(data: TSessionListItem[]) {
      if (!Array.isArray(data)) return;
      let hasChanged = false;
      let canStop = true;
      const prevRunningConversationIds = [...currentConversationIds];
      const newRunningConversationIds: string[] = [];
      data.forEach((item) => {
        const conversationId = item.conversationId || '';
        const oldItem = runningConversationInfo.current[conversationId];
        const isNowRunning = isRunning(item.status || '');
        // 状态变化 或者icon 变化，都算变了，需要更新
        if (
          oldItem?.status !== item.status ||
          oldItem?.icon?.light !== item?.icon?.light ||
          oldItem?.icon?.dark !== item?.icon?.dark
        ) {
          hasChanged = true;
          runningConversationInfo.current[conversationId] = item;
        }
        if (isNowRunning) {
          canStop = false;
          newRunningConversationIds.push(conversationId);
        }
      });
      const completedConversationIds = prevRunningConversationIds.filter(
        (id) =>
          id &&
          !newRunningConversationIds.includes(id) &&
          id !== currentConversionIdRef.current,
      );
      const toAdd = completedConversationIds.filter(
        (id) => !readConversationIdsRef.current.has(id),
      );
      if (toAdd.length) {
        setUnreadConversationIds((prev) => {
          const unreadSet = new Set(prev);
          toAdd.forEach((id) => unreadSet.add(id));
          return Array.from(unreadSet);
        });
      }
      newRunningConversationIds.forEach((id) =>
        readConversationIdsRef.current.delete(id),
      );
      currentConversationIds = newRunningConversationIds;
      setRunningConversationIds(newRunningConversationIds);
      if (hasChanged) {
        setConversationList((prev) => {
          return prev.map((item) => {
            const conversationId = item.conversationId || '';
            return runningConversationInfo.current[conversationId] || item;
          });
        });
      }
      if (canStop) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    }

    timerRef.current = setInterval(() => {
      runningCountRef.current++;
      // 模型最大时间1h,1h 后端会自动更改状态，所以前端最多轮训次数就是 1h /5s = 720 次
      if (runningCountRef.current > 720) {
        clearTimer();
        return;
      }

      postConversationDetailsApi(
        {},
        {
          conversationIds: currentConversationIds,
        },
      )
        .then((res: any) => {
          if (res?.code !== 0 || !res?.data) {
            clearTimer();
          } else {
            update(res.data);
          }
        })
        .catch(() => {
          clearTimer();
        });
    }, 5000);

    return () => {
      clearTimer();
    };
  }, [runningConversationIds, runningTag]);

  const initWebviewBridge = (webview: any) => {
    GlobalEventEmitter.on(KIM_EVENT.ADD_CONVERSATION, (data: any) => {
      loadConversations(true);
      toConversion(data.conversionId);
    });

    GlobalEventEmitter.on(KIM_EVENT.SHOW_STOP_MODAL, (data: any) => {
      modalConfirm({
        confirmBtnType: 'main',
        title: intl.t('mario.task.alert.title'),
        content: intl.t('mario.task.alert.label'),
        okText: intl.t('mario.task.alert.button'),
        cancelText: '',
        showCancelBtn: false,
      });
    });
    GlobalEventEmitter.on(KIM_EVENT.SHOW_FILE_LIST, (data: any) => {
      // showFileList(data.conversationId);
    });
    GlobalEventEmitter.on(
      KIM_EVENT.GET_CONVERSATION_DETAIL,
      ({ conversationId }: { conversationId: string }) => {
        setTimeout(() => {
          getConversationDetail(conversationId);
        }, 500);
      },
    );
  };

  useEffect(() => {
    initWebviewBridge(webviewRef.current);
  }, []);

  return {
    app,
    url,
    conversionId,
    changeUrl,
    agentClickhandler,
    toConversion,
    setConversionId,
    agentList,
    deleteAgent,
    getAgentList,
    conversationList,
    conversationPageInfo,
    loadConversations,
    setConversationList,
    renameConversation,
    deleteConversation,
    shareConversation,
    isDarkMode,
    webviewRef,
    getConversationDetail,
    unreadConversationIds,
  };
};

const { Provider: AIWindowProvider, useContainer: useAIWindowContext } =
  createContainer(useAIWindow);

export { AIWindowProvider, useAIWindowContext };
